import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Alert, App, Button, Card, Col, Input, Radio, Row, Space, Typography, Upload } from 'antd';
import { DeleteOutlined, SendOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../api/client';
import { usePermissions } from '../../auth/store';
import {
  AUDIENCE_LABEL,
  BODY_MAX,
  EMPTY_PUSH,
  pushFormError,
  pushImageProblem,
  previewBody,
  sendBody,
  TITLE_MAX,
  type PushAudience,
  type PushForm,
} from './push';

function errMsg(e: unknown, fallback: string) {
  return (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? fallback;
}

/**
 * Push notifications — compose a push (optionally with an image) and send it
 * to all users, all listeners or one user. Each send is also saved to the
 * recipients' in-app notification list.
 *
 * Super admins only: the sidebar shows it to them alone (`SUPER_ADMIN_ONLY`).
 * Any other admin opening `/push` is sent to the dashboard, and the API
 * refuses them.
 */
export function PushPage() {
  const { role } = usePermissions();
  if (role !== 'super_admin') return <Navigate to="/" replace />;
  return <PushContent />;
}

function PushContent() {
  const { message, modal } = App.useApp();
  const [form, setForm] = useState<PushForm>(EMPTY_PUSH);
  const set = (patch: Partial<PushForm>) => setForm((f) => ({ ...f, ...patch }));
  const problem = pushFormError(form);

  /** Image goes browser → storage on a short-lived URL; the server derives the key. */
  const uploadImage = useMutation({
    mutationFn: async (file: File) => {
      const bad = pushImageProblem(file);
      if (bad) throw new Error(bad);
      const { data: minted } = await api.post<{ key: string; url: string }>('/admin/push/image/upload-url', {
        contentType: file.type,
        sizeBytes: file.size,
      });
      const res = await fetch(minted.url, { method: 'PUT', headers: { 'content-type': file.type }, body: file });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      return { key: minted.key, preview: URL.createObjectURL(file) };
    },
    onSuccess: ({ key, preview }) => set({ imageKey: key, imageUrl: preview }),
    onError: (e) => message.error(e instanceof Error ? e.message : errMsg(e, 'Could not upload the image')),
  });

  const send = useMutation({
    mutationFn: async () => (await api.post<{ recipients: number }>('/admin/push/send', sendBody(form))).data,
    onSuccess: ({ recipients }) => {
      message.success(`Notification queued for ${recipients.toLocaleString()} ${recipients === 1 ? 'person' : 'people'}`);
      setForm((f) => ({ ...EMPTY_PUSH, audience: f.audience }));
    },
    onError: (e) => message.error(errMsg(e, 'Could not send the notification')),
  });

  /** Count the recipients first, then ask before anything is sent. */
  const confirmSend = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ recipients: number; user?: { handle: string; devices: number } }>(
          '/admin/push/preview',
          previewBody(form),
        )
      ).data,
    onSuccess: (p) => {
      const who =
        form.audience === 'user' && p.user
          ? `@${p.user.handle} (${p.user.devices} device${p.user.devices === 1 ? '' : 's'})`
          : `${p.recipients.toLocaleString()} ${AUDIENCE_LABEL[form.audience].replace('All ', '').toLowerCase()}`;
      if (p.recipients === 0) {
        message.warning(`No one to send to — ${who} has no registered device.`);
        return;
      }
      modal.confirm({
        title: 'Send this notification?',
        content: `It goes to ${who} right away and can't be recalled.`,
        okText: 'Send',
        onOk: () => send.mutateAsync(),
      });
    },
    onError: (e) => message.error(errMsg(e, 'Could not count the recipients')),
  });

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Push notifications
        </Typography.Title>
        <Typography.Text type="secondary">
          Send a push notification, with an optional image, to users or listeners.
        </Typography.Text>
      </div>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card>
            <Space direction="vertical" size={14} style={{ width: '100%' }}>
              <div>
                <Typography.Text strong>Send to</Typography.Text>
                <div>
                  <Radio.Group
                    value={form.audience}
                    onChange={(e) => set({ audience: e.target.value as PushAudience })}
                    optionType="button"
                    options={(Object.keys(AUDIENCE_LABEL) as PushAudience[]).map((a) => ({
                      value: a,
                      label: AUDIENCE_LABEL[a],
                    }))}
                  />
                </div>
              </div>
              {form.audience === 'user' && (
                <Input
                  aria-label="User ID or handle"
                  placeholder="User ID or @handle"
                  value={form.userRef}
                  onChange={(e) => set({ userRef: e.target.value })}
                />
              )}
              <Input
                aria-label="Title"
                placeholder="Title"
                maxLength={TITLE_MAX}
                showCount
                value={form.title}
                onChange={(e) => set({ title: e.target.value })}
              />
              <Input.TextArea
                aria-label="Message"
                placeholder="Message"
                maxLength={BODY_MAX}
                showCount
                autoSize={{ minRows: 3, maxRows: 6 }}
                value={form.body}
                onChange={(e) => set({ body: e.target.value })}
              />
              <div>
                <Typography.Text strong>Image (optional)</Typography.Text>
                <div style={{ marginTop: 6 }}>
                  <Space wrap>
                    <Upload
                      accept="image/png,image/jpeg"
                      showUploadList={false}
                      beforeUpload={(file) => {
                        uploadImage.mutate(file);
                        return false;
                      }}
                    >
                      <Button icon={<UploadOutlined />} loading={uploadImage.isPending}>
                        {form.imageKey ? 'Replace image' : 'Upload image'}
                      </Button>
                    </Upload>
                    {form.imageKey && (
                      <Button icon={<DeleteOutlined />} onClick={() => set({ imageKey: null, imageUrl: null })}>
                        Remove
                      </Button>
                    )}
                  </Space>
                  <div>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      PNG or JPEG, up to 1 MB. A wide 2:1 image (e.g. 1024×512) looks best.
                    </Typography.Text>
                  </div>
                </div>
              </div>
              <Input
                aria-label="Link"
                placeholder="Link when tapped (optional), e.g. maja://listeners/<id>"
                value={form.deepLink}
                onChange={(e) => set({ deepLink: e.target.value })}
              />
              <Alert
                type="info"
                showIcon
                message="The image shows on Android whether the app is open or closed, and on iOS while the app is open. On iOS, a notification that arrives while the app is closed shows text only."
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                disabled={problem !== null || uploadImage.isPending}
                loading={confirmSend.isPending || send.isPending}
                onClick={() => confirmSend.mutate()}
              >
                Send notification
              </Button>
              {problem && (form.title || form.body) && (
                <Typography.Text type="secondary">{problem}</Typography.Text>
              )}
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="Preview" size="small">
            <PushPreview form={form} />
          </Card>
        </Col>
      </Row>
    </Space>
  );
}

/** Roughly how an expanded Android notification shows the push. */
function PushPreview({ form }: { form: PushForm }) {
  return (
    <div
      data-testid="push-preview"
      style={{ background: '#f2f2f7', borderRadius: 16, padding: 12, border: '1px solid rgba(0,0,0,0.06)' }}
    >
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        Dude · now
      </Typography.Text>
      <div style={{ fontWeight: 600, marginTop: 2, wordBreak: 'break-word' }}>{form.title || 'Title'}</div>
      <div style={{ color: 'rgba(0,0,0,0.7)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {form.body || 'Message'}
      </div>
      {form.imageUrl && (
        <img
          src={form.imageUrl}
          alt="Notification image"
          style={{ marginTop: 8, width: '100%', aspectRatio: '2 / 1', objectFit: 'cover', borderRadius: 10 }}
        />
      )}
    </div>
  );
}
